import type { Ref } from 'vue';
import { computed } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Field } from '../types/field';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import type { PricingTable } from '../types/models';
import type { ExtendedSummary, PricingTableSummaryValue } from '../..';
import {
  getTotalDiscount,
  getTotalPriceAfterDiscount,
  getTotalPriceBeforeDiscountAndTax,
  getTotalTax,
  summarySubTotalName,
} from '../..';

export function usePricingTableSummary(pricingTable: Ref<PricingTable>, fields: Ref<Field[]>) {
  const summary = computed(() => pricingTable.value.pricingTableSummaryValues);
  const extendedSummaries = computed(() => summary.value?.extendedSummaries || []);

  const subTotal = computed(() =>
    extendedSummaries.value.find(({ name }) => name === summarySubTotalName)
  );

  const createSubTotalExtendedSummary = (): ExtendedSummary => ({
    name: summarySubTotalName,
    displayName: t('subtotal'),
    unit: 'Currency',
    enabled: true,
    value: '0',
  });

  const enabledSummaries = computed<PricingTableSummaryValue[]>(() =>
    [summary.value?.discount, summary.value?.tax, summary.value?.price].filter(
      ({ enabled }) => enabled
    )
  );
  const enabledExtendedSummaries = computed(() =>
    extendedSummaries.value.filter(({ enabled }) => enabled)
  );

  const calculatedSummary = computed(() =>
    PricingTableHelper.calculateSummaryValues(
      PricingTableHelper.sectionsWithEnabledRows(
        pricingTable.value.pricingTableSections,
        fields.value
      ),
      summary.value
    )
  );

  const hasValue = (value: string) => !!value && value !== '0';

  const getCurrenyFormattedValue = (value: string) =>
    PricingTableHelper.currencyFormat(value, pricingTable.value.currencySettings);

  const getFormattedExtendedSummaryValue = (summary: ExtendedSummary) => {
    const value = summary.value || '0';

    switch (summary.unit) {
      case 'Currency':
        return getCurrenyFormattedValue(value);
      case 'Percentage':
        return `${value}%`;
      default:
        return value;
    }
  };

  const totalPriceBeforeDiscountAndTax = computed(() =>
    getTotalPriceBeforeDiscountAndTax(pricingTable.value.pricingTableSections, fields.value)
  );

  const totalPriceAfterDiscount = computed(() =>
    getTotalPriceAfterDiscount(summary.value, pricingTable.value.pricingTableSections, fields.value)
  );

  const totalTaxText = computed(() =>
    getTotalTax(
      summary.value,
      pricingTable.value.currencySettings,
      pricingTable.value.preCalculated,
      pricingTable.value.pricingTableSections,
      fields.value
    )
  );

  const totalDiscountText = computed(() =>
    getTotalDiscount(
      summary.value,
      pricingTable.value.currencySettings,
      pricingTable.value.preCalculated,
      pricingTable.value.pricingTableSections,
      fields.value
    )
  );

  const totalPrice = computed(() =>
    PricingTableHelper.calculateTotalPrice(summary.value, pricingTable.value.pricingTableSections)
  );

  const totalPriceText = computed(() =>
    PricingTableHelper.currencyFormat(totalPrice.value, pricingTable.value.currencySettings)
  );

  return {
    summary,
    calculatedSummary,
    extendedSummaries,
    enabledSummaries,
    enabledExtendedSummaries,
    subTotal,
    getFormattedExtendedSummaryValue,
    hasValue,
    totalPriceBeforeDiscountAndTax,
    totalPriceAfterDiscount,
    totalTaxText,
    totalDiscountText,
    totalPrice,
    totalPriceText,
    createSubTotalExtendedSummary,
  };
}
